import { FC, useCallback, useMemo, useState } from 'react';
import { MRT_Row, MRT_RowData, MRT_SortingState } from 'material-react-table';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material';

import { formatNumber } from 'src/utils/utility';
import { PercentageChargeDto } from 'src/generated/services/TFinancialApi';
import { useUpsertPercentageCharge } from 'src/use-cases/serviceProvider/useServiceProvider';
import { format, parseISO } from 'date-fns';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';
import { RateCardChargesTableBase } from './TableBase';
import { getTableColumns } from './utils';
import { ITableProps } from './types';
import { CHARGE_CATEGORIES } from './constants';
import { applyFiltersAndSorting, createFilterOptions, commonBooleanAccessors } from './filterUtils';

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    paddingTop: '4px!important',
    paddingBottom: '4px!important',
    paddingLeft: '0px!important',
    paddingRight: '0px!important',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiFormHelperText-root ': {
    marginLeft: '0px!important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0',
    borderBottom: '1px solid #999999',
    borderRadius: '0',
  },
}));

export const RateCardChargesTablePercentage: FC<ITableProps> = (props) => {
  const { contractFlow, chargeCategory, tableRef, filters, setFilters, sorting, setSorting, isAdmin } = props;
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  // Original unfiltered data
  const originalData = useMemo(() => contractFlow?.[chargeCategory] ?? [], [contractFlow, chargeCategory]);

  // Define accessors for boolean fields
  const accessors = useMemo(() => commonBooleanAccessors, []);

  // Apply filters with accessors
  const tableData = useMemo(() => applyFiltersAndSorting(originalData, filters, accessors, sorting), [originalData, filters, accessors, sorting]);

  // Handle sorting and filtering
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      setSorting([{ id: columnId, desc: direction === 'desc' }]);
    },
    [setSorting]
  );

  // Handle filter changes
  const handleFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      setFilters((prev) => ({
        ...prev,
        [columnId]: selectedValues,
      }));
    },
    [setFilters]
  );

  const columns = useMemo(() => {
    const percentageColumns = [
      {
        accessorKey: 'value',
        header: 'RATE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='value'
            originalData={originalData}
            currentFilters={filters.value || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.value ? `${formatNumber(parseFloat(row?.original.value?.toString()))} %` : '-'}</>,
      },
      {
        accessorKey: 'currency',
        header: 'CURRENCY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='currency'
            originalData={originalData}
            currentFilters={filters.currency || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.currency || '-'}</>,
      },
      {
        accessorKey: 'minimumPrice',
        header: 'MINIMUM PRICE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='minimumPrice'
            originalData={originalData}
            currentFilters={filters.minimumPrice || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => (
          <>
            {row?.original.minimumPrice || parseFloat(row?.original.minimumPrice.toString()) === 0
              ? `${formatNumber(parseFloat(row?.original.minimumPrice?.toString()))}`
              : '-'}
          </>
        ),
      },
      {
        accessorKey: 'eligibleChargeDomain',
        header: 'ELIGIBLE CHARGE CATEGORY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='eligibleChargeDomain'
            originalData={originalData}
            currentFilters={filters.eligibleChargeDomain || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        editVariant: 'select',
        editSelectOptions: CHARGE_CATEGORIES,
        muiEditTextFieldProps: {
          select: true,
        },
        Cell: ({ row }) => <>{row?.original.eligibleChargeDomain || '-'}</>,
      },
      {
        accessorKey: 'eligibleChargeSubDomain',
        header: 'ELIGIBLE CHARGE NAME',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='eligibleChargeSubDomain'
            originalData={originalData}
            currentFilters={filters.eligibleChargeSubDomain || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.eligibleChargeSubDomain || '-'}</>,
      },
      {
        accessorKey: 'startDate',
        header: 'VALIDITY START DATE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='startDate'
            originalData={originalData}
            currentFilters={filters.startDate || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.startDate ? format(parseISO(row.original.startDate), 'dd-MMM-yyyy') : '-'}</>,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Edit: ({ cell, column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          return (
            <div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledDatePicker
                  defaultValue={row.original.startDate ? parseISO(row.original.startDate) : null}
                  format='dd-MMM-yyyy'
                  onChange={(value) => {
                    // eslint-disable-next-line no-underscore-dangle
                    row._valuesCache[column.id] = value;
                    if (isCreating) {
                      table.setCreatingRow(row);
                    } else if (isEditing) {
                      table.setEditingRow(row);
                    }
                  }}
                  slotProps={{
                    textField: {
                      placeholder: '10-Dec-2025',
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          );
        },
      },
      {
        accessorKey: 'endDate',
        header: 'VALIDITY END DATE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='endDate'
            originalData={originalData}
            currentFilters={filters.endDate || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => <>{row?.original.endDate ? format(parseISO(row.original.endDate), 'dd-MMM-yyyy') : '-'}</>,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Edit: ({ cell, column, row, table }) => {
          const { creatingRow, editingRow } = table.getState();
          const isCreating = creatingRow?.id === row.id;
          const isEditing = editingRow?.id === row.id;

          return (
            <div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledDatePicker
                  defaultValue={row.original.endDate ? parseISO(row.original.endDate) : null}
                  format='dd-MMM-yyyy'
                  onChange={(value) => {
                    // eslint-disable-next-line no-underscore-dangle
                    row._valuesCache[column.id] = value;
                    if (isCreating) {
                      table.setCreatingRow(row);
                    } else if (isEditing) {
                      table.setEditingRow(row);
                    }
                  }}
                  slotProps={{
                    textField: {
                      placeholder: '10-Dec-2025',
                      required: false,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          );
        },
      },
    ];

    const allColumns = getTableColumns(percentageColumns, validationErrors, setValidationErrors, {
      filters,
      handleFilter,
      handleSort: handleTableSort,
      originalData,
      createFilterOptions,
    });

    return allColumns;
  }, [validationErrors, originalData, handleFilter, handleTableSort, filters]);

  const { mutateAsync: upsertPercentageCharge } = useUpsertPercentageCharge();

  const onValidateCharge = useCallback((charge: Record<string, any>) => {
    const validationResult = {
      chargeName: !charge.chargeName || charge.chargeName === '' ? 'Required' : '',
      chargeCategory: !charge.chargeCategory || charge.chargeCategory === '' ? 'Required' : '',
      legType: !charge.legType || charge.legType === '' ? 'Required' : '',
      currency: !charge.currency || charge.currency === '' ? 'Required' : '',
      value: (!charge.value || charge.value === '') && charge.value !== 0 ? 'Required' : '',
    };

    return validationResult;
  }, []);

  const onUpsertCharge = useCallback(
    async (row: MRT_Row<MRT_RowData>, values: Record<string, any>) => {
      const reqBody: PercentageChargeDto & { contractFlowId: string; deviationExcluded?: boolean } = {
        id: row.original?.id || null,
        contractFlowId: contractFlow?.id,
        legType: values.legType,
        chargeName: values.chargeName,
        chargeCategory: values.chargeCategory,
        minimumPrice: values.minimumPrice || 0,
        currency: values?.currency || row?.original?.currency,
        accessorial: values.accessorial.toLowerCase() === 'true',
        approval: values.approval.toLowerCase() === 'true',
        outlay: values.outlay.toLowerCase() === 'true',
        deviationExcluded: values.deviationExcluded ? values.deviationExcluded.toLowerCase() === 'true' : false,
        value: values.value || null,
        eligibleChargeDomain: values.eligibleChargeDomain || null,
        startDate: values?.startDate ? format(new Date(values.startDate), 'yyyy-MM-dd') : null,
        endDate: values?.endDate ? format(new Date(values.endDate), 'yyyy-MM-dd') : null,
      };

      await upsertPercentageCharge(reqBody);
    },
    [upsertPercentageCharge, contractFlow]
  );

  return (
    <RateCardChargesTableBase
      tableRef={tableRef}
      tableData={tableData}
      columns={columns}
      sorting={sorting}
      contractFlow={contractFlow}
      chargeCategory={chargeCategory}
      onUpsertCharge={onUpsertCharge}
      setValidationErrors={setValidationErrors}
      onValidateCharge={onValidateCharge}
      isAdmin={isAdmin}
    />
  );
};
